import type { BillingAgreementStatus } from "../../shared/billing-labels";
import type { BillingOverview, BillingQuote, BillingTerms } from "../../shared/billing-schemas";

/**
 * One list of what a customer has been offered and what is in force (ADR-0077). A quote and the
 * terms its acceptance created are one item, with the terms' state once it exists; terms approved
 * without a quote are items of their own. The card renders what this module decides, and decides
 * nothing itself.
 */

export type Agreement =
  | {
      kind: "quote";
      id: string;
      status: BillingAgreementStatus;
      quote: BillingQuote;
      terms: BillingTerms | null;
      createdAt: number;
    }
  | {
      kind: "terms";
      id: string;
      status: BillingAgreementStatus;
      terms: BillingTerms;
      createdAt: number;
    };

const TERMS_STATUS: Record<BillingTerms["status"], BillingAgreementStatus> = {
  draft: "draft",
  approved: "active",
  superseded: "superseded",
  ended: "ended",
  canceled: "canceled",
};

function quoteStatus(quote: BillingQuote, terms: BillingTerms | null): BillingAgreementStatus {
  if (terms !== null) return TERMS_STATUS[terms.status];
  return quote.status === "accepted" ? "active" : quote.status;
}

function byNewest(a: Agreement, b: Agreement): number {
  if (a.createdAt !== b.createdAt) return b.createdAt - a.createdAt;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function unifyAgreements(billing: Pick<BillingOverview, "quotes" | "terms">): Agreement[] {
  const termsById = new Map(billing.terms.map((terms) => [terms.id, terms]));
  const items: Agreement[] = billing.quotes.map((quote) => {
    const terms = quote.termsId === null ? null : (termsById.get(quote.termsId) ?? null);
    return {
      kind: "quote",
      id: quote.id,
      status: quoteStatus(quote, terms),
      quote,
      terms,
      createdAt: quote.createdAt,
    };
  });
  for (const terms of billing.terms) {
    if (terms.quoteId !== null) continue;
    items.push({
      kind: "terms",
      id: terms.id,
      status: TERMS_STATUS[terms.status],
      terms,
      createdAt: terms.createdAt,
    });
  }
  return items.sort(byNewest);
}

const CURRENT = new Set<BillingAgreementStatus>(["draft", "sent", "expired", "active"]);

/** What still needs or allows a decision, and what is only history. */
export function splitAgreements(items: Agreement[]): { current: Agreement[]; past: Agreement[] } {
  return {
    current: items.filter((item) => CURRENT.has(item.status)),
    past: items.filter((item) => !CURRENT.has(item.status)),
  };
}

/** Terms an employee approved without asking the customer. */
export function isInternal(terms: BillingTerms): boolean {
  return terms.quoteId === null && terms.approvedAt !== null;
}

/** The version in force for a tool, or for the whole organization when `toolId` is null. */
export function activeTermsFor(terms: BillingTerms[], toolId: string | null): BillingTerms | null {
  return (
    terms.find(
      (version) => version.status === "approved" && (version.tool?.id ?? null) === toolId,
    ) ?? null
  );
}

/** The terms an item's active-state actions apply to. */
export function termsOf(item: Agreement): BillingTerms | null {
  return item.kind === "terms" ? item.terms : item.terms;
}

export type AgreementActionKind =
  | "send"
  | "edit"
  | "replace"
  | "cancel_quote"
  | "approve"
  | "edit_terms"
  | "end"
  | "cancel_terms";

export type AgreementActionOffer = {
  label: string;
  action: AgreementActionKind;
  primary?: boolean;
};

const CANCEL_QUOTE: AgreementActionOffer = { label: "Cancel quote", action: "cancel_quote" };
const CANCEL_TERMS: AgreementActionOffer = { label: "Cancel terms", action: "cancel_terms" };

/** What an employee who may manage billing can do to an item, in the order the buttons appear. */
export function agreementActions(item: Agreement, canManage: boolean): AgreementActionOffer[] {
  if (!canManage) return [];
  if (item.status === "active" && termsOf(item) !== null) {
    return [
      { label: "Replace with a new quote", action: "replace" },
      { label: "End", action: "end" },
      CANCEL_TERMS,
    ];
  }
  if (item.kind === "terms") {
    if (item.status !== "draft") return [];
    return [
      { label: "Approve", action: "approve", primary: true },
      { label: "Edit draft", action: "edit_terms" },
      CANCEL_TERMS,
    ];
  }
  if (item.status === "draft") {
    return [
      { label: "Send to the customer", action: "send", primary: true },
      { label: "Edit draft", action: "edit" },
      CANCEL_QUOTE,
    ];
  }
  if (item.status === "sent") {
    return [{ label: "Replace with a new quote", action: "replace" }, CANCEL_QUOTE];
  }
  if (item.status === "expired") {
    return [{ label: "Resend as a new quote", action: "replace" }, CANCEL_QUOTE];
  }
  return [];
}
